import React, { useMemo } from 'react';
import { buildLogoUrl } from '../utils/playerCardUtils';

function TeamSelectionModal({
  isOpen,
  onClose,
  teams,
  groupName,
  avoidSameCity,
  conflictingCities,
  onSelectTeam
}) {
  const filteredTeams = useMemo(() => {
    let filtered = teams;
    if (avoidSameCity && conflictingCities.size > 0) {
      filtered = filtered.filter(team => {
        if (!team.city) return true;
        const teamCity = team.city.toLowerCase().trim();
        return !Array.from(conflictingCities).some(
          city => city.toLowerCase().trim() === teamCity
        );
      });
    }
    return filtered;
  }, [teams, avoidSameCity, conflictingCities]);

  if (!isOpen) return null;

  const handleTeamClick = (team) => {
    // Check if team has city conflict
    if (avoidSameCity && team.city) {
      const teamCity = team.city.toLowerCase().trim();
      const hasConflict = Array.from(conflictingCities).some(
        city => city.toLowerCase().trim() === teamCity
      );
      if (hasConflict) {
        return; // Don't allow selection
      }
    }
    onSelectTeam(team._id);
  };

  const getTeamStatus = (team) => {
    if (!avoidSameCity || !team.city) return null;
    const teamCity = team.city.toLowerCase().trim();
    const hasConflict = Array.from(conflictingCities).some(
      city => city.toLowerCase().trim() === teamCity
    );
    return hasConflict ? 'conflict' : null;
  };

  return (
    <div className="team-selection-modal-overlay" onClick={onClose}>
      <div className="team-selection-modal team-select-window" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-inner">
            <span className="modal-group-badge">Group {groupName}</span>
            <h2 className="modal-title">Select a team</h2>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <span className="modal-close-icon">×</span>
          </button>
        </div>

        {avoidSameCity && conflictingCities.size > 0 && (
          <div className="modal-warning">
            <span className="warning-icon" aria-hidden>⚠</span>
            <span className="warning-text">
              Cities already in this group: <strong>{Array.from(conflictingCities).join(', ')}</strong>
              — teams from these cities are excluded.
            </span>
          </div>
        )}

        {filteredTeams.length === 0 ? (
          <div className="no-teams">
            <div className="no-teams-icon">◇</div>
            <p className="no-teams-title">No teams available</p>
          </div>
        ) : (
          <div className="teams-grid">
            {filteredTeams.map((team) => {
                const status = getTeamStatus(team);
                const isDisabled = status === 'conflict';

                return (
                  <button
                    key={team._id}
                    type="button"
                    className={`team-card ${isDisabled ? 'disabled' : 'clickable'}`}
                    onClick={() => !isDisabled && handleTeamClick(team)}
                    disabled={isDisabled}
                    title={
                      isDisabled
                        ? `Another team from ${team.city} is already in Group ${groupName}`
                        : `Select ${team.name}`
                    }
                  >
                    {team.logo ? (
                      <div className="team-logo">
                        <img
                          src={buildLogoUrl(team.logo)}
                          alt=""
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="team-logo team-logo-placeholder">
                        <span>{team.name?.charAt(0) || '?'}</span>
                      </div>
                    )}
                    <div className="team-info">
                      <span className="team-name">{team.name}</span>
                      {team.city && (
                        <span className="team-city">
                          {team.city}
                          {isDisabled && <span className="conflict-badge">Conflict</span>}
                        </span>
                      )}
                    </div>
                    {!isDisabled && <span className="team-card-action">Select</span>}
                    {isDisabled && (
                      <div className="team-overlay">
                        <span className="overlay-text">City conflict</span>
                      </div>
                    )}
                  </button>
                );
              })}
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="modal-footer-cancel" onClick={onClose}>
            Cancel
          </button>
          <span className="teams-count">
            {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''} available
          </span>
        </div>
      </div>
    </div>
  );
}

export default TeamSelectionModal;

